import { ModelApprovalable, RevertVoidableType } from 'api-hooks/general/model';
import { useRevertVoid } from 'api-hooks/general/mutation';
import { AuthorizationRules } from 'common/constants';
import { SaleStatusEnum } from 'common/constants/enum';
import notification from 'components/common/notification';
import { Button } from 'components/elements/buttons';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

import useApprovalAction from './use-approval-action';
import { useAuthorization } from './use-authorization';
import useDialog from './use-dialog';
import useDismiss from './use-dismiss';

export default function useRevertVoidAction(
  type: RevertVoidableType,
  data?: ModelApprovalable,
) {
  const id = data?.id;
  const status = data?.status;
  const isVoid = React.useMemo(() => {
    switch (status) {
      case SaleStatusEnum.Void:
        return true;
      default:
        return false;
    }
  }, [status]);
  const { can } = useAuthorization();
  const { t } = useTranslation();
  const { onRequestApproval } = useApprovalAction(type, data);
  const dialog = useDialog();
  const revertVoidMutate = useRevertVoid();
  const onDismiss = useDismiss(type);
  const revertVoidPermission = React.useMemo(() => {
    switch (type) {
      case 'activity-purchases':
        return can(AuthorizationRules.ActivityPurchasesRevertVoid);
      case 'activity-sales':
        return can(AuthorizationRules.ActivitySalesRevertVoid);
      case 'cruise-purchases':
        return can(AuthorizationRules.CruisePurchasesRevertVoid);
      case 'cruise-sales':
        return can(AuthorizationRules.CruiseSalesRevertVoid);
      case 'document-purchases':
        return can(AuthorizationRules.DocumentPurchasesRevertVoid);
      case 'document-sales':
        return can(AuthorizationRules.DocumentSalesRevertVoid);
      case 'flight-purchases':
        return can(AuthorizationRules.FlightPurchasesRevertVoid);
      case 'flight-sales':
        return can(AuthorizationRules.FlightSalesRevertVoid);
      case 'hotel-purchases':
        return can(AuthorizationRules.HotelPurchasesRevertVoid);
      case 'hotel-sales':
        return can(AuthorizationRules.HotelSalesRevertVoid);
      case 'product-purchases':
        return can(AuthorizationRules.ProductPurchasesRevertVoid);
      case 'product-sales':
        return can(AuthorizationRules.ProductSalesRevertVoid);
      case 'tour-sales':
        return can(AuthorizationRules.TourSalesRevertVoid);
    }
  }, [can, type]);

  const onRevertVoid = React.useCallback(
    async (id: string) => {
      const result = await revertVoidMutate.mutateAsync({ type, id });
      notification.success({
        message: result.message,
      });
      onDismiss(id);
      return result;
    },
    [onDismiss, revertVoidMutate, type],
  );

  const onClickRevertVoid = React.useCallback(
    (id: string) => () => {
      return new Promise((resolve, reject) => {
        dialog.showConfirmation({
          message: t('common:revert_void_description'),
          title: t('common:revert_void'),
          async onPositiveAction(dismiss) {
            try {
              const result = await onRevertVoid(id);
              resolve(result);
              dismiss();
              onRequestApproval(id);
            } catch (e) {
              notification.error({
                message: e.message,
              });
              reject(e);
            }
          },
          onNegativeAction(dismiss) {
            dismiss();
          },
          onAfterClose() {
            resolve(undefined);
          },
        });
      });
    },
    [dialog, onRequestApproval, onRevertVoid, t],
  );

  const revertVoidButton = revertVoidPermission && id && isVoid && (
    <Button
      onClick={onClickRevertVoid(id)}
      isLoading={revertVoidMutate.isLoading}
    >
      {t('common:revert_void')}
    </Button>
  );

  return {
    ...revertVoidMutate,
    onClickRevertVoid,
    onRevertVoid,
    revertVoidPermission,
    revertVoidButton,
    isVoid,
  };
}
